import { useEffect, useLayoutEffect, useState } from 'react';
import { useScript } from '../context/ScriptContext';

/**
 * Returns a className that produces a gentle fade whenever the language changes
 * — WITHOUT remounting the element. On a language switch the container is dimmed
 * instantly (no transition); it stays dimmed while DeepL translations are in
 * flight, then fades back to full opacity. This hides both the white flash that
 * a `key={script}` remount caused AND the pop-in/layout shift when a fetched
 * translation lands — the swap happens while the content is dimmed.
 */
export function useLangFade(): string {
  const { script, isTranslating } = useScript();
  const [dim, setDim] = useState(false);

  // Dim instantly on language change (before paint).
  useLayoutEffect(() => {
    setDim(true);
  }, [script]);

  // Lift the dim only once nothing is being translated. The short timeout gives
  // the provider a beat to flip isTranslating → true for switches that DO queue
  // DeepL calls, so we don't un-dim for one frame and re-dim (which would flicker).
  useEffect(() => {
    if (isTranslating) {
      setDim(true);
      return;
    }
    const id = window.setTimeout(() => setDim(false), 50);
    return () => window.clearTimeout(id);
  }, [script, isTranslating]);

  return dim ? 'lang-fade lang-switching' : 'lang-fade';
}
